import React, { useEffect, useMemo, useState } from 'react';
import {
  api, apiUrl, type CampaignStatus, type ChecklistItem, type CreativeStatus, type MarketingCampaign, type MarketingCampaignDetail,
  type MarketingCreative, type MarketingMetrics, type MarketingPlatform, type MarketingPlatformStatus, type MarketingResults,
} from './api';
import { t, locale } from './i18n';

// ─────────────────────────────────────────────────────────────────
// Admin → Marknadsföring (api/src/marketing). En kampanj i taget:
//  Resultat       — tratten per koncept och öppning: spenderat → besök →
//                   registreringar → aktiverade inom 7 dagar → förfrågningar/affärer
//  Annonsmaterial — film/bild, krok, annonstext, landningssida och annonslänkar
//  Plan           — budget, datum, målgrupp och checklistan för testperioden
//  Meta & TikTok  — kopplingsstatus, publicera (pausat), starta och pausa
// ─────────────────────────────────────────────────────────────────

const C = {
  text: '#111111', sub: '#4a4a4a', muted: '#8f8a8c', border: '#e8e6e7', soft: '#f7f7f7',
  primary: 'hsl(var(--primary))', primaryDim: 'hsl(var(--primary) / 0.1)', danger: 'hsl(var(--destructive))', success: '#2e7d32',
};
const input: React.CSSProperties = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 2, color: C.text, padding: '6px 10px', fontSize: 13, width: '100%', outline: 'none', fontFamily: 'inherit' };
const label: React.CSSProperties = { fontSize: 11, color: C.sub, display: 'flex', flexDirection: 'column', gap: 4 };
const card: React.CSSProperties = { border: `1px solid ${C.border}`, borderRadius: 6, padding: 14, background: '#fff' };
function btn(variant: 'primary' | 'ghost' | 'danger' = 'ghost', small = true): React.CSSProperties {
  return {
    padding: small ? '6px 12px' : '9px 18px', borderRadius: 2, fontSize: small ? 11 : 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase',
    cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
    border: variant === 'ghost' ? `1px solid ${C.border}` : 'none',
    background: variant === 'primary' ? C.primary : variant === 'danger' ? C.danger : 'transparent',
    color: variant === 'ghost' ? C.text : '#fff',
  };
}

const PLATFORM_LABEL: Record<string, string> = { meta: 'Meta', tiktok: 'TikTok' };
const CAMPAIGN_STATUSES: CampaignStatus[] = ['draft', 'live', 'paused', 'done'];
const CREATIVE_STATUSES: CreativeStatus[] = ['idea', 'production', 'ready', 'live', 'paused', 'stopped'];
const STATUS_COLOR: Record<string, string> = { idea: C.muted, production: '#b26a00', ready: '#1565c0', live: C.success, paused: '#8a8a8a', stopped: C.danger, draft: C.muted, done: '#8a8a8a' };

const nf = (digits = 0) => new Intl.NumberFormat(locale(), { maximumFractionDigits: digits, minimumFractionDigits: digits });
const kr = (v: number | null | undefined, digits = 0) => (v == null ? '–' : `${nf(digits).format(v)} kr`);
const int = (v: number | null | undefined) => (v == null ? '–' : nf(0).format(v));
const pct = (v: number | null | undefined) => (v == null ? '–' : `${nf(v < 0.1 ? 1 : 0).format(v * 100)} %`);
const mediaSrc = (url: string) => (url.startsWith('/uploads/') ? apiUrl(url) : url);
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' }) : null);

type Section = 'results' | 'creatives' | 'plan' | 'platforms';

export function AdminMarketingView() {
  const [campaigns, setCampaigns] = useState<MarketingCampaign[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<MarketingCampaignDetail | null>(null);
  const [section, setSection] = useState<Section>('results');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  async function loadList(select?: string) {
    try {
      const list = await api.marketing.campaigns();
      setCampaigns(list);
      setSelectedId(prev => select ?? (prev && list.some(c => c.id === prev) ? prev : list[0]?.id ?? null));
    } catch (e: any) { setError(e.message ?? t('marketing.loadFailed')); }
  }
  useEffect(() => { loadList(); }, []);
  useEffect(() => {
    if (!selectedId) { setCampaign(null); return; }
    api.marketing.campaign(selectedId).then(setCampaign).catch((e: any) => setError(e.message));
  }, [selectedId]);

  async function createLaunchTest() {
    setBusy(true); setError(null);
    try { const c = await api.marketing.createLaunchTest(); await loadList(c.id); setCampaign(c); setSection('creatives'); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  const onChanged = (c: MarketingCampaignDetail) => { setCampaign(c); setCampaigns(prev => prev?.map(x => x.id === c.id ? c : x) ?? prev); };
  const hasLaunchTest = campaigns?.some(c => c.utmCampaign === 'launch_test');

  const sections: { key: Section; label: string }[] = [
    { key: 'results', label: t('marketing.sections.results') },
    { key: 'creatives', label: t('marketing.sections.creatives') },
    { key: 'plan', label: t('marketing.sections.plan') },
    { key: 'platforms', label: t('marketing.sections.platforms') },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.sub, maxWidth: 620 }}>{t('marketing.intro')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {campaigns && campaigns.length > 0 && (
            <select style={{ ...input, width: 'auto', minWidth: 220 }} value={selectedId ?? ''} onChange={e => setSelectedId(e.target.value)}>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {campaigns && !hasLaunchTest && campaigns.length > 0 && <button style={btn()} disabled={busy} onClick={createLaunchTest}>{t('marketing.addLaunchTest')}</button>}
          <button style={btn(creating ? 'primary' : 'ghost')} onClick={() => setCreating(!creating)}>{t('marketing.newCampaign')}</button>
        </div>
      </div>

      {error && <div style={{ color: C.danger, fontSize: 13, marginBottom: 12 }}>{error} <button style={{ ...btn(), padding: '2px 8px', marginLeft: 8 }} onClick={() => setError(null)}>OK</button></div>}
      {creating && <NewCampaignForm onDone={async c => { setCreating(false); if (c) { await loadList(c.id); setCampaign(c); setSection('creatives'); } }} />}

      {!campaigns ? <div style={{ fontSize: 13, color: C.muted }}>{t('common.loading')}</div>
        : campaigns.length === 0 ? (
          <div style={{ ...card, padding: 28, textAlign: 'center' }}>
            <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 22, marginBottom: 8 }}>{t('marketing.empty.title')}</div>
            <div style={{ fontSize: 13, color: C.sub, maxWidth: 520, margin: '0 auto 16px' }}>{t('marketing.empty.body')}</div>
            <button style={btn('primary', false)} disabled={busy} onClick={createLaunchTest}>{busy ? t('marketing.working') : t('marketing.empty.cta')}</button>
          </div>
        ) : !campaign ? <div style={{ fontSize: 13, color: C.muted }}>{t('common.loading')}</div> : (
          <>
            <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 18, overflowX: 'auto' }}>
              {sections.map(s => (
                <button key={s.key} onClick={() => setSection(s.key)} style={{
                  background: 'none', border: 'none', borderBottom: `2px solid ${section === s.key ? C.text : 'transparent'}`, padding: '8px 12px',
                  fontSize: 13, fontWeight: section === s.key ? 700 : 500, color: section === s.key ? C.text : C.sub, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>{s.label}</button>
              ))}
              <span style={{ marginLeft: 'auto', alignSelf: 'center' }}><StatusPill status={campaign.status} /></span>
            </div>
            {section === 'results' && <ResultsSection campaign={campaign} />}
            {section === 'creatives' && <CreativesSection campaign={campaign} onChanged={onChanged} onError={setError} />}
            {section === 'plan' && <PlanSection campaign={campaign} onChanged={onChanged} onError={setError} onDeleted={() => { setCampaign(null); loadList(); }} />}
            {section === 'platforms' && <PlatformsSection campaign={campaign} onChanged={onChanged} onError={setError} />}
          </>
        )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  return <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#fff', background: STATUS_COLOR[status] ?? C.muted, padding: '3px 8px', borderRadius: 2 }}>
    {t(`marketing.status.${status}` as any)}
  </span>;
}

function NewCampaignForm({ onDone }: { onDone: (c?: MarketingCampaignDetail) => void }) {
  const [name, setName] = useState('');
  const [utm, setUtm] = useState('');
  const [budget, setBudget] = useState('');
  const [daily, setDaily] = useState('');
  const [error, setError] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      onDone(await api.marketing.createCampaign({ name, utmCampaign: utm || name, budgetKr: budget ? Number(budget) : null, dailyBudgetKr: daily ? Number(daily) : null } as any));
    } catch (err: any) { setError(err.message); }
  }
  return (
    <form onSubmit={submit} style={{ ...card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
      <label style={{ ...label, flex: '2 1 220px' }}>{t('marketing.plan.name')}<input style={input} value={name} onChange={e => setName(e.target.value)} required autoFocus /></label>
      <label style={{ ...label, flex: '1 1 140px' }}>utm_campaign<input style={input} value={utm} onChange={e => setUtm(e.target.value)} placeholder="t.ex. vinter_test" /></label>
      <label style={{ ...label, width: 110 }}>{t('marketing.plan.budget')}<input style={input} type="number" min={0} value={budget} onChange={e => setBudget(e.target.value)} /></label>
      <label style={{ ...label, width: 110 }}>{t('marketing.plan.dailyBudget')}<input style={input} type="number" min={0} value={daily} onChange={e => setDaily(e.target.value)} /></label>
      <button type="submit" style={btn('primary')} disabled={!name.trim()}>{t('marketing.create')}</button>
      {error && <div style={{ color: C.danger, fontSize: 12, flexBasis: '100%' }}>{error}</div>}
    </form>
  );
}

// ── Resultat ─────────────────────────────────────────────────────

function sum(rows: MarketingMetrics[]): MarketingMetrics {
  const keys = ['spend', 'impressions', 'clicks', 'videoViews2s', 'videoViews6s', 'platformLandingViews', 'visits', 'registrations', 'activated7d', 'activationPending', 'requests', 'transactions'] as const;
  const m = Object.fromEntries(keys.map(k => [k, rows.reduce((s, r) => s + (r[k] ?? 0), 0)])) as any as MarketingMetrics;
  const per = (d: number) => (d > 0 ? m.spend / d : null);
  const settled = m.registrations - m.activationPending;
  return { ...m, ctr: m.impressions ? m.clicks / m.impressions : null, costPerVisit: per(m.visits || m.platformLandingViews), costPerRegistration: per(m.registrations), costPerActivated: per(m.activated7d), activationRate: settled > 0 ? m.activated7d / settled : null };
}

function Kpi({ label: l, value, sub, highlight }: { label: string; value: string; sub?: string | null; highlight?: boolean }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: '12px 14px', background: highlight ? C.primaryDim : '#fff' }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{l}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ResultsSection({ campaign }: { campaign: MarketingCampaignDetail }) {
  const [results, setResults] = useState<MarketingResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncInfo, setSyncInfo] = useState<string | null>(null);
  const [byConcept, setByConcept] = useState(false);

  const load = () => api.marketing.results(campaign.id).then(r => { setResults(r); setError(null); }).catch((e: any) => setError(e.message));
  useEffect(() => { load(); }, [campaign.id]);

  async function sync() {
    setSyncing(true); setSyncInfo(null);
    try {
      const { results: rs } = await api.marketing.sync();
      setSyncInfo(rs.map(r => `${PLATFORM_LABEL[r.platform]}: ${r.skipped ? t('marketing.results.notConnected') : r.error ? r.error : t('marketing.results.rowsSynced', { count: r.rows ?? 0 })}`).join(' · '));
      await load();
    } catch (e: any) { setSyncInfo(e.message); } finally { setSyncing(false); }
  }

  const rows = useMemo(() => {
    if (!results) return [];
    if (!byConcept) return results.variants.map(v => ({ key: v.variantKey, title: v.hook ?? v.variantKey, subtitle: `${v.concept} · ${v.variantKey}`, status: v.status as string | null, m: v as MarketingMetrics }));
    const concepts = [...new Set(results.variants.map(v => v.concept))];
    return concepts.map(c => ({ key: c, title: c, subtitle: results.variants.filter(v => v.concept === c).map(v => v.variantKey).join(', '), status: null, m: sum(results.variants.filter(v => v.concept === c)) }));
  }, [results, byConcept]);

  if (error) return <div style={{ color: C.danger, fontSize: 13 }}>{error}</div>;
  if (!results) return <div style={{ fontSize: 13, color: C.muted }}>{t('common.loading')}</div>;
  const tot = results.total;
  const otherHasData = results.unattributed.spend > 0 || results.unattributed.visits > 0 || results.unattributed.registrations > 0;
  const lastSync = Object.entries(results.lastSync).filter(([, at]) => at).map(([p, at]) => `${PLATFORM_LABEL[p]} ${when(at)}`).join(' · ');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: C.muted }}>{lastSync ? t('marketing.results.lastSync', { when: lastSync }) : t('marketing.results.neverSynced')}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn()} onClick={() => setByConcept(!byConcept)}>{byConcept ? t('marketing.results.perOpening') : t('marketing.results.perConcept')}</button>
          <button style={btn('primary')} disabled={syncing} onClick={sync}>{syncing ? t('marketing.working') : t('marketing.results.sync')}</button>
        </div>
      </div>
      {syncInfo && <div style={{ fontSize: 12, color: C.sub, marginBottom: 10 }}>{syncInfo}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 18 }}>
        <Kpi label={t('marketing.kpi.spend')} value={kr(tot.spend)} sub={campaign.budgetKr != null ? t('marketing.kpi.ofBudget', { budget: kr(campaign.budgetKr), left: kr(results.budgetLeft) }) : null} />
        <Kpi label={t('marketing.kpi.visits')} value={int(tot.visits)} sub={t('marketing.kpi.perVisit', { cost: kr(tot.costPerVisit, 2) })} />
        <Kpi label={t('marketing.kpi.registrations')} value={int(tot.registrations)} sub={results.unverifiedRegistrations ? t('marketing.kpi.unverified', { count: results.unverifiedRegistrations }) : null} />
        <Kpi label={t('marketing.kpi.costPerRegistration')} value={kr(tot.costPerRegistration)} highlight />
        <Kpi label={t('marketing.kpi.activated')} value={int(tot.activated7d)} sub={t('marketing.kpi.activationRate', { rate: pct(tot.activationRate), pending: tot.activationPending })} />
        <Kpi label={t('marketing.kpi.costPerActivated')} value={kr(tot.costPerActivated)} highlight />
        <Kpi label={t('marketing.kpi.requests')} value={int(tot.requests)} sub={t('marketing.kpi.transactions', { count: tot.transactions })} />
      </div>

      {results.daily.length > 0 && <DailyChart daily={results.daily} />}

      <div style={{ overflowX: 'auto', marginBottom: 14 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 980 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${C.text}`, textAlign: 'right', color: C.sub }}>
              <th style={{ ...th, textAlign: 'left' }}>{byConcept ? t('marketing.results.concept') : t('marketing.results.variant')}</th>
              <th style={th}>{t('marketing.results.spend')}</th>
              <th style={th}>{t('marketing.results.impressions')}</th>
              <th style={th} title={t('marketing.results.hookRateHint')}>{t('marketing.results.hookRate')}</th>
              <th style={th}>{t('marketing.results.clicks')}</th>
              <th style={th}>CTR</th>
              <th style={th} title={t('marketing.results.visitsHint')}>{t('marketing.results.visits')}</th>
              <th style={th}>{t('marketing.results.perVisit')}</th>
              <th style={th}>{t('marketing.results.registrations')}</th>
              <th style={th}>{t('marketing.results.perRegistration')}</th>
              <th style={th} title={t('marketing.results.activatedHint')}>{t('marketing.results.activated')}</th>
              <th style={th}>{t('marketing.results.requests')}</th>
              <th style={th}>{t('marketing.results.transactions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => <ResultRow key={r.key} title={r.title} subtitle={r.subtitle} status={r.status} m={r.m} />)}
            {otherHasData && <ResultRow title={t('marketing.results.unknownVariant')} subtitle={t('marketing.results.unknownVariantHint')} status={null} m={results.unattributed} muted />}
            <ResultRow title={t('marketing.results.total')} subtitle="" status={null} m={tot} bold />
          </tbody>
        </table>
      </div>

      {results.platforms.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {results.platforms.map(p => (
            <div key={p.platform} style={{ ...card, padding: '10px 14px', fontSize: 12, minWidth: 220 }}>
              <strong>{PLATFORM_LABEL[p.platform] ?? p.platform}</strong>
              <div style={{ color: C.sub, marginTop: 4 }}>{t('marketing.results.platformLine', { spend: kr(p.spend), visits: int(p.visits), regs: int(p.registrations), cpr: kr(p.costPerRegistration) })}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...card, background: C.soft, border: 'none', fontSize: 12, color: C.sub, lineHeight: 1.6 }}>
        <strong style={{ color: C.text }}>{t('marketing.results.howToRead')}</strong> {t('marketing.results.decisionRule')}
      </div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '8px 6px', fontWeight: 600, whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '8px 6px', textAlign: 'right', whiteSpace: 'nowrap' };

function ResultRow({ title, subtitle, status, m, bold, muted }: { title: string; subtitle: string; status: string | null; m: MarketingMetrics; bold?: boolean; muted?: boolean }) {
  return (
    <tr style={{ borderBottom: `1px solid ${C.border}`, fontWeight: bold ? 700 : 400, color: muted ? C.muted : C.text, background: bold ? C.soft : undefined }}>
      <td style={{ padding: '8px 6px', maxWidth: 280 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{status && <span title={t(`marketing.status.${status}` as any)} style={{ width: 8, height: 8, borderRadius: 4, background: STATUS_COLOR[status], flexShrink: 0 }} />}<span>{title}</span></div>
        {subtitle && <div style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>{subtitle}</div>}
      </td>
      <td style={td}>{kr(m.spend)}</td>
      <td style={td}>{int(m.impressions)}</td>
      <td style={td}>{pct(m.impressions ? m.videoViews2s / m.impressions : null)}</td>
      <td style={td}>{int(m.clicks)}</td>
      <td style={td}>{pct(m.ctr)}</td>
      <td style={td}>{int(m.visits)}{m.platformLandingViews > 0 && <span style={{ color: C.muted }}> / {int(m.platformLandingViews)}</span>}</td>
      <td style={td}>{kr(m.costPerVisit, 2)}</td>
      <td style={td}>{int(m.registrations)}</td>
      <td style={td}>{kr(m.costPerRegistration)}</td>
      <td style={td}>{int(m.activated7d)} <span style={{ color: C.muted }}>({pct(m.activationRate)})</span></td>
      <td style={td}>{int(m.requests)}</td>
      <td style={td}>{int(m.transactions)}</td>
    </tr>
  );
}

function DailyChart({ daily }: { daily: MarketingResults['daily'] }) {
  const W = 760, H = 140, pad = 24;
  const maxSpend = Math.max(1, ...daily.map(d => d.spend));
  const maxRegs = Math.max(1, ...daily.map(d => d.registrations));
  const bw = Math.min(28, (W - pad * 2) / daily.length - 4);
  const x = (i: number) => pad + i * ((W - pad * 2) / daily.length) + 2;
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: C.sub, marginBottom: 6 }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: C.primary, opacity: 0.35, marginRight: 4 }} />{t('marketing.chart.spend')}</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: C.text, marginRight: 4 }} />{t('marketing.chart.registrations')}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H + 18}`} style={{ width: '100%', maxWidth: W, height: 'auto', display: 'block' }} role="img" aria-label={t('marketing.chart.label')}>
        {daily.map((d, i) => {
          const h = (d.spend / maxSpend) * (H - 10);
          const cy = H - (d.registrations / maxRegs) * (H - 14) - 4;
          return (
            <g key={d.day}>
              <rect x={x(i)} y={H - h} width={bw} height={h} fill="hsl(var(--primary))" opacity={0.35}><title>{`${d.day}: ${kr(d.spend)}, ${d.visits} besök, ${d.registrations} reg.`}</title></rect>
              {d.registrations > 0 && <circle cx={x(i) + bw / 2} cy={cy} r={4} fill={C.text}><title>{`${d.day}: ${d.registrations}`}</title></circle>}
              {(daily.length <= 14 || i % Math.ceil(daily.length / 14) === 0) && <text x={x(i) + bw / 2} y={H + 13} fontSize={9} textAnchor="middle" fill={C.muted}>{d.day.slice(5)}</text>}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Annonsmaterial ───────────────────────────────────────────────

function CreativesSection({ campaign, onChanged, onError }: { campaign: MarketingCampaignDetail; onChanged: (c: MarketingCampaignDetail) => void; onError: (e: string) => void }) {
  const [adding, setAdding] = useState(false);
  const concepts = [...new Set(campaign.creatives.map(c => c.concept))];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-start' }}>
        <div style={{ fontSize: 12, color: C.sub, maxWidth: 640 }}>{t('marketing.creatives.intro', { code: campaign.inviteCode })}</div>
        <button style={btn(adding ? 'primary' : 'ghost')} onClick={() => setAdding(!adding)}>{t('marketing.creatives.add')}</button>
      </div>
      {adding && <NewCreativeForm campaign={campaign} concepts={concepts} onDone={c => { setAdding(false); if (c) onChanged(c); }} />}
      {concepts.map(concept => (
        <div key={concept} style={{ marginBottom: 22 }}>
          <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 18, marginBottom: 8 }}>{concept}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
            {campaign.creatives.filter(c => c.concept === concept).map(c => <CreativeCard key={c.id} creative={c} onChanged={onChanged} onError={onError} />)}
          </div>
        </div>
      ))}
      {campaign.creatives.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>{t('marketing.creatives.none')}</div>}
    </div>
  );
}

function NewCreativeForm({ campaign, concepts, onDone }: { campaign: MarketingCampaignDetail; concepts: string[]; onDone: (c?: MarketingCampaignDetail) => void }) {
  const [concept, setConcept] = useState(concepts[0] ?? '');
  const [variantKey, setVariantKey] = useState('');
  const [hook, setHook] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Ny öppning i ett befintligt koncept: samma text, CTA och landningssida som syskonen.
  const sibling = campaign.creatives.find(c => c.concept === concept);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      onDone(await api.marketing.createCreative(campaign.id, {
        concept, variantKey, hook, sortOrder: campaign.creatives.length,
        ...(sibling ? { adText: sibling.adText, cta: sibling.cta, landingPath: sibling.landingPath, desiredAction: sibling.desiredAction, script: sibling.script } : {}),
      }));
    } catch (err: any) { setError(err.message); }
  }
  return (
    <form onSubmit={submit} style={{ ...card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
      <label style={{ ...label, flex: '1 1 180px' }}>{t('marketing.creatives.concept')}
        <input style={input} value={concept} onChange={e => setConcept(e.target.value)} list="mk-concepts" required />
        <datalist id="mk-concepts">{concepts.map(c => <option key={c} value={c} />)}</datalist>
      </label>
      <label style={{ ...label, width: 140 }}>{t('marketing.creatives.variantKey')}<input style={input} value={variantKey} onChange={e => setVariantKey(e.target.value)} placeholder="friends_c" required /></label>
      <label style={{ ...label, flex: '2 1 220px' }}>{t('marketing.creatives.hook')}<input style={input} value={hook} onChange={e => setHook(e.target.value)} /></label>
      <button type="submit" style={btn('primary')}>{t('marketing.create')}</button>
      {error && <div style={{ color: C.danger, fontSize: 12, flexBasis: '100%' }}>{error}</div>}
    </form>
  );
}

function CreativeCard({ creative: c, onChanged, onError }: { creative: MarketingCreative; onChanged: (c: MarketingCampaignDetail) => void; onError: (e: string) => void }) {
  const [draft, setDraft] = useState(c);
  const [uploading, setUploading] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => setDraft(c), [c]);

  async function save(patch: Partial<MarketingCreative>) {
    try { onChanged(await api.marketing.updateCreative(c.id, patch)); } catch (e: any) { onError(e.message); setDraft(c); }
  }
  const saveField = (k: keyof MarketingCreative) => () => { if ((draft as any)[k] !== (c as any)[k]) save({ [k]: (draft as any)[k] }); };

  async function upload(file: File) {
    setUploading(true);
    try {
      const res = await api.uploadFile(file);
      const url = res.url.includes('/uploads/') ? res.url.slice(res.url.indexOf('/uploads/')) : res.url;
      await save({ mediaUrl: url, mediaType: res.type === 'video' ? 'video' : 'image', ...(c.status === 'idea' || c.status === 'production' ? { status: 'ready' as CreativeStatus } : {}) });
    } catch (e: any) { onError(e.message); } finally { setUploading(false); }
  }
  async function copy(platform: MarketingPlatform) {
    try { await navigator.clipboard.writeText(c.links[platform]); setCopied(platform); setTimeout(() => setCopied(null), 1500); } catch { /* ignorera */ }
  }
  async function toggleAd(platform: MarketingPlatform, running: boolean) {
    if (running && !confirm(t('marketing.creatives.confirmStartAd', { platform: PLATFORM_LABEL[platform], variant: c.variantKey }))) return;
    try { onChanged(await api.marketing.setAdRunning(c.id, platform, running)); } catch (e: any) { onError(e.message); }
  }
  async function remove() {
    if (!confirm(t('marketing.creatives.confirmDelete', { variant: c.variantKey }))) return;
    try { onChanged(await api.marketing.deleteCreative(c.id)); } catch (e: any) { onError(e.message); }
  }
  const field = (k: 'hook' | 'adText' | 'cta' | 'landingPath', l: string, multiline = false) => (
    <label style={label}>{l}
      {multiline
        ? <textarea style={{ ...input, minHeight: 54, resize: 'vertical' }} value={(draft[k] as string) ?? ''} onChange={e => setDraft({ ...draft, [k]: e.target.value })} onBlur={saveField(k)} />
        : <input style={input} value={(draft[k] as string) ?? ''} onChange={e => setDraft({ ...draft, [k]: e.target.value })} onBlur={saveField(k)} />}
    </label>
  );

  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ width: 96, height: 170, background: C.soft, borderRadius: 4, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {c.mediaUrl && c.mediaType === 'video' && <video src={mediaSrc(c.mediaUrl)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted loop playsInline controls preload="metadata" />}
          {c.mediaUrl && c.mediaType === 'image' && <img src={mediaSrc(c.mediaUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
          {!c.mediaUrl && <span style={{ fontSize: 10, color: C.muted, textAlign: 'center', padding: 6 }}>{t('marketing.creatives.noMedia')}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
            <code style={{ fontSize: 12, fontWeight: 700 }}>{c.variantKey}</code>
            <select style={{ ...input, width: 'auto', padding: '3px 6px', fontSize: 11, color: STATUS_COLOR[draft.status] }} value={draft.status} onChange={e => { setDraft({ ...draft, status: e.target.value as CreativeStatus }); save({ status: e.target.value as CreativeStatus }); }}>
              {CREATIVE_STATUSES.map(s => <option key={s} value={s}>{t(`marketing.status.${s}` as any)}</option>)}
            </select>
          </div>
          {field('hook', t('marketing.creatives.hook'))}
          <label style={{ ...btn(), justifyContent: 'center', opacity: uploading ? 0.6 : 1 }}>
            {uploading ? t('marketing.creatives.uploading') : c.mediaUrl ? t('marketing.creatives.replaceMedia') : t('marketing.creatives.uploadMedia')}
            <input type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" style={{ display: 'none' }} disabled={uploading}
              onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
          </label>
          <div style={{ fontSize: 10, color: C.muted }}>{t('marketing.creatives.mediaSpec')}</div>
        </div>
      </div>
      {field('adText', t('marketing.creatives.adText'), true)}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {field('cta', t('marketing.creatives.cta'))}
        {field('landingPath', t('marketing.creatives.landingPath'))}
      </div>
      {c.desiredAction && <div style={{ fontSize: 11, color: C.sub }}><strong>{t('marketing.creatives.desiredAction')}</strong> {c.desiredAction}</div>}
      {c.script && (
        <div>
          <button style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, color: C.sub, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowScript(!showScript)}>
            {showScript ? t('marketing.creatives.hideScript') : t('marketing.creatives.showScript')}
          </button>
          {showScript && <div style={{ whiteSpace: 'pre-wrap', fontSize: 11, color: C.sub, background: C.soft, padding: 8, marginTop: 6, borderRadius: 4 }}>{c.script}</div>}
        </div>
      )}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(['meta', 'tiktok'] as MarketingPlatform[]).map(p => {
          const adId = p === 'meta' ? c.metaAdId : c.tiktokAdId;
          return (
            <div key={p} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11 }}>
              <strong style={{ width: 48 }}>{PLATFORM_LABEL[p]}</strong>
              <button style={{ ...btn(), padding: '3px 8px', fontSize: 10 }} onClick={() => copy(p)} title={c.links[p]}>{copied === p ? t('marketing.creatives.copied') : t('marketing.creatives.copyLink')}</button>
              {adId ? (
                <>
                  <span style={{ color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('marketing.creatives.adId', { id: adId })}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                    <button style={{ ...btn(), padding: '3px 8px', fontSize: 10 }} onClick={() => toggleAd(p, false)}>{t('marketing.pause')}</button>
                    <button style={{ ...btn(), padding: '3px 8px', fontSize: 10 }} onClick={() => toggleAd(p, true)}>{t('marketing.start')}</button>
                  </span>
                </>
              ) : <span style={{ color: C.muted }}>{t('marketing.creatives.notPublished')}</span>}
            </div>
          );
        })}
      </div>
      <button style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, color: C.danger, cursor: 'pointer', alignSelf: 'flex-end' }} onClick={remove}>{t('marketing.creatives.delete')}</button>
    </div>
  );
}

// ── Plan ─────────────────────────────────────────────────────────

function PlanSection({ campaign, onChanged, onError, onDeleted }: { campaign: MarketingCampaignDetail; onChanged: (c: MarketingCampaignDetail) => void; onError: (e: string) => void; onDeleted: () => void }) {
  const [draft, setDraft] = useState(campaign);
  const [newItem, setNewItem] = useState({ when: '', text: '' });
  useEffect(() => setDraft(campaign), [campaign]);

  async function save(patch: Partial<MarketingCampaign>) {
    try { onChanged(await api.marketing.updateCampaign(campaign.id, patch)); } catch (e: any) { onError(e.message); setDraft(campaign); }
  }
  const blur = (k: keyof MarketingCampaign) => () => { if ((draft as any)[k] !== (campaign as any)[k]) save({ [k]: (draft as any)[k] === '' ? null : (draft as any)[k] }); };
  const setChecklist = (list: ChecklistItem[]) => { setDraft({ ...draft, checklist: list }); save({ checklist: list }); };
  const groups = [...new Set(draft.checklist.map(i => i.when))];
  const done = draft.checklist.filter(i => i.done).length;

  async function remove() {
    if (!confirm(t('marketing.plan.confirmDelete', { name: campaign.name }))) return;
    try { await api.marketing.deleteCampaign(campaign.id); onDeleted(); } catch (e: any) { onError(e.message); }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18, alignItems: 'start' }}>
      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label style={label}>{t('marketing.plan.name')}<input style={input} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} onBlur={blur('name')} /></label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={label}>{t('marketing.plan.status')}
            <select style={input} value={draft.status} onChange={e => { setDraft({ ...draft, status: e.target.value as CampaignStatus }); save({ status: e.target.value as CampaignStatus }); }}>
              {CAMPAIGN_STATUSES.map(s => <option key={s} value={s}>{t(`marketing.status.${s}` as any)}</option>)}
            </select>
          </label>
          <label style={label}>utm_campaign<input style={input} value={draft.utmCampaign} onChange={e => setDraft({ ...draft, utmCampaign: e.target.value })} onBlur={blur('utmCampaign')} /></label>
          <label style={label}>{t('marketing.plan.budget')}<input style={input} type="number" min={0} value={draft.budgetKr ?? ''} onChange={e => setDraft({ ...draft, budgetKr: e.target.value === '' ? null : Number(e.target.value) })} onBlur={blur('budgetKr')} /></label>
          <label style={label}>{t('marketing.plan.dailyBudget')}<input style={input} type="number" min={0} value={draft.dailyBudgetKr ?? ''} onChange={e => setDraft({ ...draft, dailyBudgetKr: e.target.value === '' ? null : Number(e.target.value) })} onBlur={blur('dailyBudgetKr')} /></label>
          <label style={label}>{t('marketing.plan.startDate')}<input style={input} type="date" value={draft.startDate ?? ''} onChange={e => setDraft({ ...draft, startDate: e.target.value })} onBlur={blur('startDate')} /></label>
          <label style={label}>{t('marketing.plan.endDate')}<input style={input} type="date" value={draft.endDate ?? ''} onChange={e => setDraft({ ...draft, endDate: e.target.value })} onBlur={blur('endDate')} /></label>
        </div>
        <label style={label}>{t('marketing.plan.audience')}<input style={input} value={draft.audience ?? ''} onChange={e => setDraft({ ...draft, audience: e.target.value })} onBlur={blur('audience')} /></label>
        <label style={label}>{t('marketing.plan.notes')}<textarea style={{ ...input, minHeight: 110, resize: 'vertical' }} value={draft.notes ?? ''} onChange={e => setDraft({ ...draft, notes: e.target.value })} onBlur={blur('notes')} /></label>
        <div style={{ fontSize: 12, color: C.sub }}>{t('marketing.plan.inviteCode')} <code style={{ fontWeight: 700, color: C.text }}>{campaign.inviteCode}</code></div>
        <button style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, color: C.danger, cursor: 'pointer', alignSelf: 'flex-start' }} onClick={remove}>{t('marketing.plan.delete')}</button>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <strong style={{ fontSize: 14 }}>{t('marketing.plan.checklist')}</strong>
          <span style={{ fontSize: 12, color: C.muted }}>{done}/{draft.checklist.length}</span>
        </div>
        {groups.map(g => (
          <div key={g} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: C.muted, marginBottom: 4 }}>{g || t('marketing.plan.noWhen')}</div>
            {draft.checklist.filter(i => i.when === g).map(item => (
              <label key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, padding: '4px 0', cursor: 'pointer', color: item.done ? C.muted : C.text }}>
                <input type="checkbox" checked={item.done} style={{ marginTop: 3 }} onChange={() => setChecklist(draft.checklist.map(i => i.id === item.id ? { ...i, done: !i.done } : i))} />
                <span style={{ flex: 1, textDecoration: item.done ? 'line-through' : 'none' }}>
                  {item.text}{item.owner && <span style={{ color: C.muted, fontSize: 11 }}> · {item.owner}</span>}
                </span>
                <button title={t('marketing.plan.removeItem')} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, padding: 0 }}
                  onClick={e => { e.preventDefault(); setChecklist(draft.checklist.filter(i => i.id !== item.id)); }}>×</button>
              </label>
            ))}
          </div>
        ))}
        <form style={{ display: 'flex', gap: 6, marginTop: 6 }} onSubmit={e => { e.preventDefault(); if (!newItem.text.trim()) return; setChecklist([...draft.checklist, { id: Math.random().toString(36).slice(2, 10), when: newItem.when.trim(), text: newItem.text.trim(), done: false }]); setNewItem({ when: newItem.when, text: '' }); }}>
          <input style={{ ...input, width: 100 }} placeholder={t('marketing.plan.when')} value={newItem.when} onChange={e => setNewItem({ ...newItem, when: e.target.value })} list="mk-when" />
          <datalist id="mk-when">{groups.map(g => <option key={g} value={g} />)}</datalist>
          <input style={input} placeholder={t('marketing.plan.newItem')} value={newItem.text} onChange={e => setNewItem({ ...newItem, text: e.target.value })} />
          <button type="submit" style={btn()}>+</button>
        </form>
      </div>
    </div>
  );
}

// ── Meta & TikTok ────────────────────────────────────────────────

function PlatformsSection({ campaign, onChanged, onError }: { campaign: MarketingCampaignDetail; onChanged: (c: MarketingCampaignDetail) => void; onError: (e: string) => void }) {
  const [statuses, setStatuses] = useState<MarketingPlatformStatus[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  useEffect(() => { api.marketing.platforms().then(setStatuses).catch((e: any) => onError(e.message)); }, []);

  const readyFor = (p: MarketingPlatform) => campaign.creatives.filter(c => c.mediaUrl && ['ready', 'live'].includes(c.status) && !(p === 'meta' ? c.metaAdId : c.tiktokAdId) && (p === 'meta' || c.mediaType === 'video'));

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key); setInfo(null);
    try { await fn(); } catch (e: any) { onError(e.message); } finally { setBusy(null); }
  }
  const publish = (p: MarketingPlatform) => run(`publish-${p}`, async () => {
    const res = await api.marketing.publish(campaign.id, p);
    setInfo(t('marketing.platforms.published', { platform: PLATFORM_LABEL[p], count: res.created.length }) + (res.skipped.length ? ` ${t('marketing.platforms.skippedImages', { variants: res.skipped.join(', ') })}` : ''));
    onChanged(await api.marketing.campaign(campaign.id));
  });
  const setRunning = (p: MarketingPlatform, running: boolean) => {
    if (running && !confirm(t('marketing.platforms.confirmStart', { platform: PLATFORM_LABEL[p], daily: kr(campaign.dailyBudgetKr), budget: kr(campaign.budgetKr) }))) return;
    run(`${running ? 'start' : 'pause'}-${p}`, async () => { onChanged(await api.marketing.setRunning(campaign.id, p, running)); setInfo(running ? t('marketing.platforms.started', { platform: PLATFORM_LABEL[p] }) : t('marketing.platforms.paused', { platform: PLATFORM_LABEL[p] })); });
  };

  if (!statuses) return <div style={{ fontSize: 13, color: C.muted }}>{t('common.loading')}</div>;
  return (
    <div>
      <div style={{ fontSize: 12, color: C.sub, maxWidth: 680, marginBottom: 14 }}>{t('marketing.platforms.intro')}</div>
      {info && <div style={{ fontSize: 12, color: C.success, marginBottom: 12 }}>{info}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
        {statuses.map(s => {
          const p = s.platform;
          const ids = p === 'meta' ? campaign.meta : campaign.tiktok;
          const published = Boolean(ids.campaignId);
          const ready = readyFor(p);
          return (
            <div key={p} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 16 }}>{PLATFORM_LABEL[p]}</strong>
                <span style={{ fontSize: 11, fontWeight: 700, color: !s.configured ? C.muted : s.ok ? C.success : C.danger }}>
                  {!s.configured ? t('marketing.platforms.notConnected') : s.ok ? t('marketing.platforms.connected') : t('marketing.platforms.problem')}
                </span>
              </div>
              {s.configured && s.account && <div style={{ fontSize: 12, color: C.sub }}>{t('marketing.platforms.account', { account: s.account, currency: s.currency ?? '?' })}</div>}
              {s.currency && s.currency !== 'SEK' && <div style={{ fontSize: 12, color: C.danger }}>{t('marketing.platforms.currencyWarning', { currency: s.currency })}</div>}
              {s.error && <div style={{ fontSize: 12, color: C.danger }}>{s.error}</div>}
              {!s.configured && (
                <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6 }}>
                  {t('marketing.platforms.setup')} {s.missing.map(m => <code key={m} style={{ background: C.soft, padding: '1px 4px', marginRight: 4 }}>{m}</code>)}
                  <div style={{ marginTop: 4 }}>{p === 'meta' ? t('marketing.platforms.metaSetup') : t('marketing.platforms.tiktokSetup')}</div>
                </div>
              )}
              <div style={{ fontSize: 12, color: C.sub }}>
                {published ? t('marketing.platforms.publishedIds', { campaign: ids.campaignId ?? '–' }) : t('marketing.platforms.notPublished')}
                <div>{t('marketing.platforms.readyCount', { count: ready.length })}</div>
              </div>
              {s.configured && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={btn('primary')} disabled={!!busy || ready.length === 0} onClick={() => publish(p)}>
                    {busy === `publish-${p}` ? t('marketing.working') : published ? t('marketing.platforms.publishMore') : t('marketing.platforms.publish')}
                  </button>
                  {published && <button style={btn()} disabled={!!busy} onClick={() => setRunning(p, true)}>{busy === `start-${p}` ? t('marketing.working') : t('marketing.start')}</button>}
                  {published && <button style={btn()} disabled={!!busy} onClick={() => setRunning(p, false)}>{busy === `pause-${p}` ? t('marketing.working') : t('marketing.pause')}</button>}
                </div>
              )}
              <a href={p === 'meta' ? 'https://adsmanager.facebook.com/adsmanager/manage/campaigns' : 'https://ads.tiktok.com/i18n/perf/campaign'} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: C.sub }}>
                {t('marketing.platforms.openManager', { platform: PLATFORM_LABEL[p] })} ↗
              </a>
            </div>
          );
        })}
      </div>
      <div style={{ ...card, background: C.soft, border: 'none', fontSize: 12, color: C.sub, lineHeight: 1.6, marginTop: 16 }}>{t('marketing.platforms.manualHint', { utm: campaign.utmCampaign })}</div>
    </div>
  );
}
